"use client";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useFormik } from "formik";
import * as yup from "yup";
import "yup-phone-lite";
// LOCAL CUSTOM COMPONENTS

import EyeToggleButton from "../components/eye-toggle-button";
// LOCAL CUSTOM HOOK

import BoxLink from "../components/box-link";
import usePasswordVisible from "../use-password-visible";
// GLOBAL CUSTOM COMPONENTS

import {Paragraph, Span} from "@/components/Typography";
import { FlexBox } from "@/components/flex-box";
import BazaarTextField from "@/components/BazaarTextField";
import { useEffect } from "react";
import { FormHelperText } from "@mui/material";
import { router } from "@inertiajs/react";
import GoogleIcon from "@mui/icons-material/Google.js";

const RegisterPageView = ({disableRegistration}) => {
    const { visiblePassword, togglePasswordVisible } = usePasswordVisible();
    // COMMON INPUT PROPS FOR TEXT FIELD

    const inputProps = {
        endAdornment: (
            <EyeToggleButton
                show={visiblePassword}
                click={togglePasswordVisible}
            />
        ),
    };
    // REGISTER FORM FIELDS INITIAL VALUES

    const initialValues = {
        name: "",
        email: "",
        phone: "",
        password: "",
        password_confirmation: "",
        voucher: "",
        agreement: false,
    };
    // REGISTER FORM FIELD VALIDATION SCHEMA

    const validationSchema = yup.object().shape({
        name: yup.string().required("Name is required"),
        email: yup
            .string()
            .email("invalid email")
            .required("Email is required"),
        phone: yup
            .string()
            .phone("MY", "Please enter a valid phone number")
            .required("A phone number is required"),
        password: yup.string().required("Password is required"),
        password_confirmation: yup
            .string()
            .oneOf([yup.ref("password"), null], "Passwords must match")
            .required("Please re-type password"),
        agreement: yup
            .bool()
            .test(
                "agreement",
                "You have to agree with our Terms and Conditions!",
                (value) => value === true,
            )
            .required("You have to agree with our Terms and Conditions!"),
    });
    const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
        useFormik({
            initialValues,
            validationSchema,
            onSubmit: (values, helpers) => {
                console.log(values);
                router.post(route("register"), values, {
                    onError: (errors) => {
                        console.log(errors);
                        helpers.setErrors(errors);
                    },
                });
            },
        });
    useEffect(() => {
        console.log(errors, touched);
    }, [errors.agreement]);
    return (
        <form onSubmit={handleSubmit}>
            {/*<Paragraph sx={{pb: 3, textAlign: 'center'}}>Currently public registrations are closed. You may only proceed if you have a valid voucher code.</Paragraph>*/}
            <BazaarTextField
                mb={1.5}
                fullWidth
                name="name"
                size="small"
                label="Full Name"
                variant="outlined"
                onBlur={handleBlur}
                value={values.name}
                onChange={handleChange}
                placeholder="Enter your name"
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
            />

            <BazaarTextField
                mb={1.5}
                fullWidth
                name="email"
                size="small"
                type="email"
                variant="outlined"
                onBlur={handleBlur}
                value={values.email}
                onChange={handleChange}
                label="Email"
                placeholder="Enter your email"
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
            />

            <BazaarTextField
                mb={1.5}
                fullWidth
                name="phone"
                size="small"
                variant="outlined"
                onBlur={handleBlur}
                value={values.phone}
                onChange={handleChange}
                label="Phone"
                placeholder="Enter your phone number"
                error={!!touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
            />

            <BazaarTextField
                mb={1.5}
                fullWidth
                size="small"
                name="password"
                label="Password"
                variant="outlined"
                autoComplete="on"
                placeholder="*********"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password}
                type={visiblePassword ? "text" : "password"}
                error={!!touched.password && !!errors.password}
                helperText={touched.password && errors.password}
                InputProps={inputProps}
            />

            <BazaarTextField
                mb={1.5}
                fullWidth
                size="small"
                autoComplete="on"
                name="password_confirmation"
                variant="outlined"
                label="Confirm Password"
                placeholder="*********"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password_confirmation}
                type={visiblePassword ? "text" : "password"}
                error={
                    !!touched.password_confirmation &&
                    !!errors.password_confirmation
                }
                helperText={
                    touched.password_confirmation &&
                    errors.password_confirmation
                }
                InputProps={inputProps}
            />

            <BazaarTextField
                mb={1.5}
                fullWidth
                name="voucher"
                size="small"
                label="Voucher Code (Optional)"
                variant="outlined"
                onBlur={handleBlur}
                value={values.voucher}
                onChange={handleChange}
                placeholder="Enter your voucher code if you have one"
                error={!!touched.voucher && !!errors.voucher}
                helperText={touched.voucher && errors.voucher}
            />

            <FormControlLabel
                name="agreement"
                className="agreement"
                onChange={handleChange}
                control={
                    <Checkbox
                        size="small"
                        color="secondary"
                        checked={values.agreement || false}
                    />
                }
                label={
                    <FlexBox
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="flex-start"
                        gap={1}
                    >
                        <Span display={{
                            sm: "inline-block",
                            xs: "none"
                        }}>By signing up, you agree to our</Span>
                        <Span display={{
                            sm: "none",
                            xs: "inline-block"
                        }}>Accept Our</Span>
                        <a
                            href="/terms-of-service"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "underline" }}
                        >
                            Terms of Service
                        </a>
                    </FlexBox>
                }
            />
            {errors.agreement && touched.agreement && (
                <FlexBox
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent="center"
                >
                    <FormHelperText
                        style={{
                            marginBottom: "2rem",
                            color: "red",
                            fontWeight: "600",
                        }}
                    >
                        {errors.agreement}
                    </FormHelperText>
                </FlexBox>
            )}
            <FlexBox
                justifyContent="space-between"
                alignItems="center"
                flexDirection="column"
                gap={1}
            >
                <Button
                    fullWidth
                    type="submit"
                    color="primary"
                    variant="contained"
                    size="large"
                >
                    Create Account
                </Button>
                {
                    !disableRegistration && (
                        <a
                            href={route("google.redirect")}
                            style={{
                                display: "block",
                                width: "100%",
                                textDecoration: "none",
                            }}
                        >
                            <Button
                                fullWidth
                                color="primary"
                                variant="outlined"
                                size="large"
                            >
                                Sign up with <GoogleIcon sx={{ml: 1}}/>
                            </Button>
                        </a>
                    )
                }
            </FlexBox>
        </form>
    );
};

export default RegisterPageView;
